/**
 * Silo Anywhere — the Silo Settings rail card (P3).
 *
 * Lives in the right rail under "Your blind link", which is where Randy drew
 * it. The rail is one of three columns, so this card carries only what reads
 * well narrow: state, mode, the embed code, and the way in to the full-width
 * design panel. Every colour control is in that panel.
 *
 * Locked state uses the ONE platform upgrade popup via `openGate`. No second
 * checkout, no bespoke upsell card.
 *
 * Saves on change. The silo form's sticky "Save settings" bar does NOT cover
 * this card, so the card says so rather than leaving a toggle that looks
 * unsaved.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChatCircleDots, Copy, Sliders, ArrowsClockwise, Prohibit,
  Lock, ArrowRight, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useEntitlements } from "@/lib/entitlements";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import SiloAnywherePanel from "@/components/SiloAnywherePanel";
import { useTrialKeepPrompt } from "@/lib/trial";

const SKU = "silo_anywhere_widget";
const BASE_SKU = "min_workbench";
const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(0)}`;
// Fuel is usually fractions of a cent per question, so a whole-dollar format
// would read "$0" all day and tell the owner nothing.
const fuel = (cents) => {
  const c = Number(cents || 0);
  if (!c) return "$0.00";
  return c < 100 ? `$${(c / 100).toFixed(3)}` : `$${(c / 100).toFixed(2)}`;
};

const findNode = (catalog, key) => {
  for (const s of catalog?.suites || []) {
    for (const it of s.items || []) {
      if (it.key === key) return it;
      for (const ch of it.children || []) if (ch.key === key) return ch;
    }
  }
  return null;
};

function LockedCard() {
  const { openGate } = useCreatorProGate();
  const { owns } = useEntitlements();
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/creator-pro/catalog")
      .then((r) => { if (alive) setCatalog(r.data?.catalog || r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const widget = findNode(catalog, SKU);
  const base = findNode(catalog, BASE_SKU);
  const ownsBase = owns(BASE_SKU);
  const notYet = !!widget?.needs_code;
  // Randy's decision 1: the shared pricing logic is untouched, so the required
  // base still lands in the basket for anyone who doesn't own it. Both lines
  // and the real total are itemized HERE, before anyone reaches Stripe.
  const lines = [
    { label: widget?.name || "Silo Anywhere Widget", cents: widget?.day1_cents || 0, need: true },
    ...(ownsBase ? [] : [{ label: base?.name || "Minimum Workbench", cents: base?.day1_cents || 0, need: true }]),
  ];
  const total = lines.reduce((a, l) => a + l.cents, 0);

  return (
    <div className="bg-white border hairline rounded-lg p-6" data-testid="silo-anywhere-locked">
      <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] flex items-center gap-2">
        <Lock size={12} weight="fill" /> Silo Anywhere
      </div>
      <div className="mt-2 text-sm font-medium text-[#1C1B1A]">
        Put your trained Silo assistant on any website.
      </div>
      <p className="text-xs text-[#6B6A68] mt-1.5">
        Drop one line of code on your own site. Visitors ask your silo questions without leaving
        the page, open your full Silo experience in a tab, and find My Silo through your referral
        link.
      </p>

      {catalog && (
        <div className="mt-4 rounded-md bg-[#F0F1EE] border hairline p-3" data-testid="silo-anywhere-price-breakdown">
          {lines.map((l) => (
            <div key={l.label} className="flex items-center justify-between text-xs py-0.5">
              <span className="text-[#1C1B1A]">{l.label}</span>
              <span className="text-[#1C1B1A] tabular-nums">{fmt$(l.cents)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between text-xs pt-2 mt-1.5 border-t hairline font-medium">
            <span className="text-[#1C1B1A]">Total today</span>
            <span className="text-[#1C1B1A] tabular-nums" data-testid="silo-anywhere-price-total">{fmt$(total)}</span>
          </div>
          {!ownsBase && (
            <p className="text-[10.5px] text-[#6B6A68] mt-2">
              Minimum Workbench is the required Creator Pro foundation, so it is included for
              anyone who does not already own it. Creator Pro members pay {fmt$(widget?.day1_cents)}.
            </p>
          )}
        </div>
      )}

      {notYet ? (
        <div className="mt-4 text-[11.5px] text-[#6B6A68] flex items-start gap-1.5" data-testid="silo-anywhere-not-yet">
          <CheckCircle size={12} weight="duotone" className="mt-0.5 shrink-0 text-[#4A5D4E]" />
          <span>
            Registered and priced, not on sale yet. It goes on sale once the widget clears its
            security, entitlement, referral, rate-limit and mobile checks.
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => openGate(SKU)}
          data-testid="silo-anywhere-unlock-btn"
          className="mt-4 w-full px-4 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm flex items-center justify-center gap-1.5"
        >
          Unlock Silo Anywhere <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}

export default function SiloAnywhereCard({ silo }) {
  useTrialKeepPrompt(SKU);
  const { loading: entLoading, locked } = useEntitlements();
  const [cfg, setCfg] = useState(null);
  const [state, setState] = useState("loading");   // loading | ready | hidden
  const [busy, setBusy] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [askAck, setAskAck] = useState(false);     // the sponsorship disclosure

  const isLocked = !entLoading && locked(SKU);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/silos/${silo.id}/widget`);
      setCfg(r.data);
      setState("ready");
    } catch (e) {
      // 402 is the paywall (handled by isLocked), 403 a non-owner, both of
      // which have their own rendering. Anything else: stay quiet rather than
      // putting an error card in the middle of Settings.
      setState("hidden");
    }
  }, [silo.id]);

  useEffect(() => {
    if (entLoading) return;
    if (isLocked) { setState("locked"); return; }
    load();
  }, [entLoading, isLocked, load]);

  const patch = async (body, okMsg) => {
    setBusy("patch");
    try {
      const r = await api.patch(`/silos/${silo.id}/widget`, body);
      setCfg(r.data);
      if (okMsg) toast.success(okMsg);
      // P7 — the assistant may be live on the page the owner is standing on, so
      // any change to enablement, mode or scope has to reach it immediately.
      try { window.dispatchEvent(new CustomEvent("mysilo:silo-anywhere-changed")); } catch { /* */ }
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that.");
    } finally {
      setBusy("");
    }
  };

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(cfg.snippet);
      toast.success("Embed code copied. Paste it before </body> on your site.");
    } catch {
      toast.error("Could not reach the clipboard. Select the code and copy it by hand.");
    }
  };

  const rotate = async () => {
    if (!window.confirm("Issue a new embed code?\n\nThe code currently on your website will stop working until you paste the new one.")) return;
    setBusy("rotate");
    try {
      const r = await api.post(`/silos/${silo.id}/widget/rotate`);
      setCfg(r.data);
      try { window.dispatchEvent(new CustomEvent("mysilo:silo-anywhere-changed")); } catch { /* */ }
      toast.success("New embed code issued. Paste it on your site to bring the widget back.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not rotate the code.");
    } finally { setBusy(""); }
  };

  const revoke = async () => {
    if (!window.confirm("Shut the widget off everywhere?\n\nIt switches off AND the current embed code is destroyed, so a copy pasted on someone else's site dies immediately. You will need the new code to turn it back on.")) return;
    setBusy("revoke");
    try {
      const r = await api.post(`/silos/${silo.id}/widget/revoke`);
      setCfg(r.data);
      try { window.dispatchEvent(new CustomEvent("mysilo:silo-anywhere-changed")); } catch { /* */ }
      toast.success("Widget off and the old embed code is dead.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not revoke.");
    } finally { setBusy(""); }
  };

  const statusLine = useMemo(() => {
    if (!cfg) return "";
    if (!cfg.enabled) return cfg.revoked_at ? "Off · code revoked" : "Off";
    return cfg.mode === "launcher" ? "On · opens My Silo in a new tab" : "On · chat on your website";
  }, [cfg]);

  // Embedded Assistant means YOU sponsor the conversation: no visitor dots, no
  // subscription, your fuel. The server refuses to switch it on until that has
  // been acknowledged, so the toggle asks first rather than failing afterwards.
  const needsAck = !!cfg && cfg.mode === "chat" && !cfg.sponsorship_ack;

  const toggleEnabled = () => {
    if (cfg.enabled) { patch({ enabled: false }, "Widget switched off."); return; }
    if (needsAck) { setAskAck(true); return; }
    patch({ enabled: true }, "Widget switched on.");
  };

  const confirmAck = async () => {
    const r = await patch({ enabled: true, owner_sponsored_ack: true }, "Widget switched on.");
    if (r) setAskAck(false);
  };

  const setMode = (v) => {
    if (v === "chat" && cfg.enabled && !cfg.sponsorship_ack) { setAskAck(true); return; }
    patch({ mode: v });
  };

  if (entLoading || state === "loading") {
    return (
      <div className="bg-white border hairline rounded-lg p-6" data-testid="silo-anywhere-loading">
        <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">Silo Anywhere</div>
        <div className="mt-2 text-xs text-[#6B6A68]">Loading…</div>
      </div>
    );
  }
  if (state === "locked") return <LockedCard />;
  if (state === "hidden" || !cfg) return null;

  return (
    <div className="bg-white border hairline rounded-lg p-6" data-testid="silo-anywhere-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] flex items-center gap-2">
            <ChatCircleDots size={12} weight="duotone" /> Silo Anywhere
          </div>
          <div className="mt-1.5 text-sm text-[#1C1B1A]" data-testid="silo-anywhere-status">{statusLine}</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!!cfg.enabled}
          aria-label="Enable Silo Anywhere"
          disabled={!!busy}
          onClick={toggleEnabled}
          data-testid="silo-anywhere-enable-toggle"
          className={`shrink-0 w-11 h-6 rounded-full transition-colors relative ${cfg.enabled ? "bg-[#4A5D4E]" : "bg-[#D6D3CA]"} disabled:opacity-60`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.enabled ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>

      <div className="mt-4">
        <div className="text-xs text-[#6B6A68] mb-1.5">Mode</div>
        <div className="inline-flex rounded-md border hairline overflow-hidden w-full" role="radiogroup" aria-label="Widget mode">
          {[
            { v: "chat", l: "Chat here" },
            { v: "launcher", l: "New tab" },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              role="radio"
              aria-checked={cfg.mode === o.v}
              disabled={!!busy}
              onClick={() => setMode(o.v)}
              data-testid={`silo-anywhere-mode-${o.v}`}
              className={`flex-1 px-3 py-1.5 text-xs transition-colors ${
                cfg.mode === o.v ? "bg-[#1C1B1A] text-[#FDFCF8]" : "bg-white text-[#6B6A68] hover:text-[#1C1B1A]"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
        <p className="text-[10.5px] text-[#6B6A68] mt-1.5">
          {cfg.mode === "chat"
            ? "Visitors ask on your site. They don't spend dots and don't need your subscription — you sponsor it."
            : "An ordinary link to your full Public Q&A page, where your normal dots and subscription rules apply."}
        </p>
      </div>

      {askAck && (
        <div className="mt-4 rounded-md border border-[#4A5D4E]/40 bg-[#F0F1EE] p-3" data-testid="silo-anywhere-ack">
          <div className="text-xs font-medium text-[#1C1B1A]">Before you turn this on</div>
          <p className="text-[11.5px] text-[#6B6A68] leading-relaxed mt-1.5" data-testid="silo-anywhere-ack-copy">
            {cfg.sponsorship_copy}
          </p>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={confirmAck} disabled={!!busy} data-testid="silo-anywhere-ack-confirm"
              className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60">
              I understand — turn it on
            </button>
            <button type="button" onClick={() => setAskAck(false)} disabled={!!busy} data-testid="silo-anywhere-ack-cancel"
              className="text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E]">
              Not yet
            </button>
          </div>
        </div>
      )}

      {cfg.enabled && cfg.mode === "chat" && cfg.usage && (
        <dl className="mt-4 rounded-md bg-[#F0F1EE] border hairline divide-y hairline" data-testid="silo-anywhere-usage">
          {[
            ["Questions today", String(cfg.usage.asks_today ?? 0), "usage-asks"],
            ["Daily limit", String(cfg.usage.daily_limit ?? 0), "usage-limit"],
            ["Fuel used today", fuel(cfg.usage.cost_cents_today), "usage-fuel"],
            ["Blocked today", String(cfg.usage.blocked_today ?? 0), "usage-blocked"],
          ].map(([k, v, tid]) => (
            <div key={k} className="flex items-center justify-between px-3 py-1.5">
              <dt className="text-[11.5px] text-[#6B6A68]">{k}</dt>
              <dd className="text-[11.5px] text-[#1C1B1A] tabular-nums" data-testid={`silo-anywhere-${tid}`}>{v}</dd>
            </div>
          ))}
        </dl>
      )}

      {/* P7 — the same widget, on this silo's own creator pages, for testing and
          tuning. Deliberately narrow: owner + collaborators, inside this silo
          only. Never the Public Q&A page, never the dashboard. */}
      {cfg.mode === "chat" && cfg.enabled && (
        <div className="mt-4 rounded-md border hairline bg-[#FAFAF7] p-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs text-[#1C1B1A]">Show it on this silo's pages</div>
            <p className="text-[10.5px] text-[#6B6A68] mt-1 leading-relaxed">
              Meet your assistant inside your own silo — Library, Workbench, Settings — exactly as
              a visitor meets it on your website. Only you and your collaborators see it, your own
              questions have their own daily allowance, and it never appears on your Public Q&amp;A
              page or anywhere else on My Silo.
            </p>
            {cfg.usage && cfg.scope?.site_widget && (
              <p className="text-[10.5px] text-[#4A5D4E] mt-1.5 tabular-nums" data-testid="silo-anywhere-selfhost-usage">
                Your testing today: {cfg.usage.preview_today ?? 0} of {cfg.usage.preview_limit ?? 0} ·{" "}
                {fuel(cfg.usage.preview_cost_cents_today)} fuel
              </p>
            )}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!cfg.scope?.site_widget}
            aria-label="Show the assistant on this silo's pages"
            disabled={!!busy}
            onClick={() => patch(
              { scope: { site_widget: !cfg.scope?.site_widget } },
              cfg.scope?.site_widget
                ? "Hidden on this silo's pages."
                : "Showing on this silo's pages. Open any tab to try it.",
            )}
            data-testid="silo-anywhere-selfhost-toggle"
            className={`shrink-0 w-11 h-6 rounded-full transition-colors relative ${cfg.scope?.site_widget ? "bg-[#4A5D4E]" : "bg-[#D6D3CA]"} disabled:opacity-60`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.scope?.site_widget ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
      )}

      <div className="mt-4">
        <div className="text-xs text-[#6B6A68] mb-1.5">Embed code</div>
        <pre
          className="text-[10.5px] leading-relaxed text-[#1C1B1A] font-mono whitespace-pre-wrap break-all bg-[#F0F1EE] rounded px-3 py-2 max-h-28 overflow-y-auto"
          data-testid="silo-anywhere-snippet"
        >{cfg.snippet}</pre>
        <div className="mt-2 flex gap-2">
          <button type="button" onClick={copySnippet} data-testid="silo-anywhere-copy"
            className="text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
            <Copy size={12} /> Copy
          </button>
          <button type="button" onClick={() => setPanelOpen(true)} data-testid="silo-anywhere-customize"
            className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5">
            <Sliders size={12} /> Design &amp; preview
          </button>
        </div>
        <p className="text-[10.5px] text-[#6B6A68] mt-2">
          Colours, greeting and limits save from the design panel and reach your site instantly.
          This code only changes if you issue a new one below.
        </p>
      </div>

      <div className="mt-4 pt-3 border-t hairline flex flex-wrap gap-2">
        <button type="button" onClick={rotate} disabled={!!busy} data-testid="silo-anywhere-rotate"
          className="text-[11px] px-2.5 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] disabled:opacity-60 flex items-center gap-1.5">
          <ArrowsClockwise size={11} /> New code
        </button>
        <button type="button" onClick={revoke} disabled={!!busy} data-testid="silo-anywhere-revoke"
          className="text-[11px] px-2.5 py-1.5 rounded-md border border-[#C16E5A]/40 text-[#8A3A1C] hover:bg-[#C16E5A]/[0.07] disabled:opacity-60 flex items-center gap-1.5">
          <Prohibit size={11} /> Revoke
        </button>
      </div>
      <p className="text-[10.5px] text-[#6B6A68] mt-2">
        This card saves on change. The Save settings bar below belongs to the silo form, not here.
      </p>

      <SiloAnywherePanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        siloId={silo.id}
        config={cfg}
        onSaved={setCfg}
      />
    </div>
  );
}
